import {
  Box,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useFetch from '../../api/useFetch';
import TypeSkeleton from '../../components/form/form-fields/TypeSkeleton';
import Header from '../../components/special/Header';
import { tokens } from '../../global/theme/tokens';
import UserTimeOverview from './user-entries/UserTimeOverview';
import { useEffect, useState } from 'react';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { DBRequest, useConfig } from '../../api/api';
import moment from 'moment';
import Cookies from 'universal-cookie';
import TimeManagerHistory from './history/TimeManagerHistory';
import EmployeeFilter from './history/EmployeeFilter';
import { VacationOverview } from './vacation/VacationOverview';
import WorkTimeReport from './report/WorkTimeReport';
import WorkTimeReportToolbar from './report/WorkTimeReportToolbar';
import { exportWorkTimeReportCsv } from './report/workTimeReportCsv';
import { clampDateRange } from './report/workTimeReportUtils';

const cookies = new Cookies();
const COOKIE_TAB = 'timeManagerTab';
const SESSION_FILTER = 'timeManagerEmployeeFilter';
const SESSION_REPORT_RANGE = 'timeManagerReportDateRange';
const SESSION_REPORT_SORT = 'timeManagerReportSort';

const TimeManager = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const config = useConfig();

  const [apiData, setApiData] = useState(null);
  const [activeTab, setActiveTab] = useState('time');

  const [period, setPeriod] = useState('day');
  const [startDate, setStartDate] = useState(moment().format());
  const [viewMode, setViewMode] = useState(
    () => cookies.get(COOKIE_TAB) || 'timeline',
  );
  const [employeeFilter, setEmployeeFilter] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_FILTER);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [reportRange, setReportRange] = useState(() => {
    try {
      const saved = sessionStorage.getItem(SESSION_REPORT_RANGE);
      if (saved) return clampDateRange(JSON.parse(saved));
    } catch {
      // fall through
    }
    return clampDateRange({
      startDate: moment().startOf('year').format('YYYY-MM-DD'),
      endDate: moment().endOf('year').format('YYYY-MM-DD'),
    });
  });
  const [reportSearch, setReportSearch] = useState('');
  const [reportSort, setReportSort] = useState(
    () => sessionStorage.getItem(SESSION_REPORT_SORT) || 'customer',
  );
  const [exportRequestId, setExportRequestId] = useState(0);

  useEffect(() => {
    reqData(moment().format(), 'day');
  }, [config]);

  const periodMap = {
    day: {
      key: 'days',
      value: 7,
      format: 'ddd',
      prefix: '',
      endOf: 'day',
      startOf: 'day',
    },
    week: {
      key: 'weeks',
      value: 6,
      format: 'w',
      prefix: t('week'),
      endOf: 'week',
      startOf: 'week',
    },
    month: {
      key: 'months',
      value: 6,
      format: 'MMM',
      prefix: '',
      endOf: 'month',
      startOf: 'month',
    },
  };

  function handleChange(e) {
    const value = e.target.value;
    if (value === 'history') {
      setViewMode('history');
      cookies.set(COOKIE_TAB, 'history', { path: '/' });
      return;
    }
    if (value === 'report') {
      setViewMode('report');
      cookies.set(COOKIE_TAB, 'report', { path: '/' });
      return;
    }
    setViewMode('timeline');
    cookies.set(COOKIE_TAB, 'timeline', { path: '/' });
    setPeriod(value);
    reqData(moment().format(), value);
  }

  function handleEmployeeFilterChange(selected) {
    setEmployeeFilter(selected);
    sessionStorage.setItem(SESSION_FILTER, JSON.stringify(selected));
  }

  function handleReportRangeChange(next) {
    const clamped = clampDateRange(next);
    setReportRange(clamped);
    sessionStorage.setItem(SESSION_REPORT_RANGE, JSON.stringify(clamped));
  }

  function handleReportSortChange(next) {
    setReportSort(next);
    sessionStorage.setItem(SESSION_REPORT_SORT, next);
  }

  function handleExportRequest() {
    setExportRequestId((prev) => prev + 1);
  }

  function handleExportReady(entries) {
    exportWorkTimeReportCsv({
      entries,
      startDate: reportRange.startDate,
      endDate: reportRange.endDate,
    });
  }

  function addPeriod() {
    const newStartDate = moment(startDate).add(
      periodMap[period].value,
      periodMap[period].key,
    );
    setStartDate(newStartDate);
    reqData(newStartDate, period);
  }

  function subtractPeriod() {
    const newStartDate = moment(startDate).subtract(
      periodMap[period].value,
      periodMap[period].key,
    );
    setStartDate(newStartDate);
    reqData(newStartDate, period);
  }

  function reqData(defaultStartDate, reqPeriod) {
    const reqEndDate = moment(defaultStartDate)
      .endOf(periodMap[reqPeriod].endOf)
      .format('YYYY-MM-DD');
    const reqStartDate = moment(defaultStartDate)
      .startOf(periodMap[reqPeriod].startOf)
      .subtract(periodMap[reqPeriod].value, periodMap[reqPeriod].key)
      .format('YYYY-MM-DD');

    setApiData(null);

    DBRequest({
      config,
      path: `users/time-tracking/overview?period=${reqPeriod}&startDate=${reqStartDate}&endDate=${reqEndDate}`,
      onResponse: setApiData,
    });
  }

  const isHistory = viewMode === 'history';
  const isReport = viewMode === 'report';

  return (
    <>
      <Box className="flex gap-0 md:gap-8 md:items-center justify-between md:justify-start pr-2 md:flex-row flex-col pb-4 md:pb-0">
        <Header title={t('Time Manager')} />
        <ToggleButtonGroup
          size="small"
          value={activeTab}
          exclusive
          onChange={(e) => setActiveTab(e.target.value)}
          aria-label="Project Status"
          className="backdrop-blur-lg"
        >
          <ToggleButton value="time">{t('Times')}</ToggleButton>
          <ToggleButton value="vacation">{t('Vacations')}</ToggleButton>
        </ToggleButtonGroup>
      </Box>
      {activeTab === 'time' && (
        <>
          <Box
            className="flex justify-between items-center px-2 py-1 rounded-lg backdrop-blur-lg sticky top-[55px] z-20 mb-2 gap-2"
            sx={{
              bgcolor: colors.glass,
            }}
          >
            {isHistory ? (
              <EmployeeFilter
                value={employeeFilter}
                onChange={handleEmployeeFilterChange}
              />
            ) : isReport ? (
              <WorkTimeReportToolbar
                startDate={reportRange.startDate}
                endDate={reportRange.endDate}
                onDateRangeChange={handleReportRangeChange}
                search={reportSearch}
                onSearchChange={setReportSearch}
                sortKey={reportSort}
                onSortChange={handleReportSortChange}
                onExportCsv={handleExportRequest}
                hasData
              />
            ) : (
              <IconButton onClick={subtractPeriod}>
                <KeyboardArrowLeftIcon />
              </IconButton>
            )}
            <Box className="flex items-center gap-3">
              <ToggleButtonGroup
                size="small"
                value={isHistory || isReport ? null : period}
                exclusive
                onChange={handleChange}
                aria-label="Time Period"
              >
                <ToggleButton value="day">{t('Day')}</ToggleButton>
                <ToggleButton value="week">{t('Week')}</ToggleButton>
                <ToggleButton value="month">{t('Month')}</ToggleButton>
              </ToggleButtonGroup>
              <ToggleButtonGroup
                size="small"
                value={isHistory ? 'history' : isReport ? 'report' : null}
                exclusive
                onChange={handleChange}
                aria-label="View Mode"
              >
                <ToggleButton value="history">{t('Historie')}</ToggleButton>
                <ToggleButton value="report">{t('Report')}</ToggleButton>
              </ToggleButtonGroup>
            </Box>
            {isHistory || isReport ? (
              <Box sx={{ width: 40 }} />
            ) : (
              <IconButton onClick={addPeriod}>
                <KeyboardArrowRightIcon />
              </IconButton>
            )}
          </Box>
          {isHistory ? (
            <TimeManagerHistory employeeFilter={employeeFilter} />
          ) : isReport ? (
            <WorkTimeReport
              startDate={reportRange.startDate}
              endDate={reportRange.endDate}
              search={reportSearch}
              sortKey={reportSort}
              exportRequestId={exportRequestId}
              onExportReady={handleExportReady}
            />
          ) : apiData ? (
            <Box
              className="flex flex-col h-full pb-2 md:pb-0 overflow-auto px-1 sm:px-0 rounded-lg backdrop-blur-lg"
              sx={{
                bgcolor: colors.glass,
              }}
            >
              {apiData.data.map((data, index) => (
                <UserTimeOverview
                  key={index}
                  data={data}
                  period={period}
                  format={periodMap[period].format}
                  prefix={periodMap[period].prefix}
                />
              ))}
            </Box>
          ) : (
            <TypeSkeleton n={10} />
          )}
        </>
      )}
      {activeTab === 'vacation' && <VacationOverview />}
    </>
  );
};

export default TimeManager;
